import { css } from 'styled-components';

const button = css`
  color: var(--white-new);
  background-color: transparent;
  border: 1px solid var(--white-new);
  border-radius: var(--border-radius);
  font-size: var(--fz-xs);
  font-family: var(--font-mono);
  line-height: 1;
  text-decoration: none;
  padding: 1.25rem 1.75rem;
  transition: all 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  will-change: transform, box-shadow;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
    transition: left 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover,
  &:focus-visible {
    outline: none;
    box-shadow: 3px 3px 0 0 var(--white-new);
    transform: translate(-3px, -3px) scale(1.02);

    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translate(-1px, -1px) scale(0.98);
    box-shadow: 1px 1px 0 0 var(--white-new);
    transition: all 100ms ease-out;
  }

  &:after {
    display: none !important;
  }
`;

const mixins = {
  flexCenter: css`
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  flexBetween: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,

  link: css`
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--white-new);
      outline: 0;
    }
  `,

  inlineLink: css`
    display: inline-block;
    position: relative;
    color: var(--white-new);
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--white-new);
      outline: 0;
      &:after {
        width: 100%;
      }
      & > * {
        color: var(--white-new) !important;
        transition: var(--transition);
      }
    }
    &:after {
      content: '';
      display: block;
      width: 0;
      height: 1px;
      position: relative;
      bottom: 0.37em;
      background-color: var(--white-new);
      opacity: 0.5;
      @media (prefers-reduced-motion: no-preference) {
        transition: var(--transition);
      }
    }
  `,

  button,

  smallButton: css`
    color: var(--white-new);
    background-color: transparent;
    border: 1px solid var(--white-new);
    border-radius: var(--border-radius);
    padding: 0.75rem 1rem;
    font-size: var(--fz-xs);
    font-family: var(--font-mono);
    line-height: 1;
    text-decoration: none;
    transition: all 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    overflow: hidden;
    will-change: transform, box-shadow;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
      transition: left 280ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    &:hover,
    &:focus-visible {
      outline: none;
      box-shadow: 2px 2px 0 0 var(--white-new);
      transform: translate(-2px, -2px) scale(1.02);

      &:before {
        left: 100%;
      }
    }

    &:active {
      transform: translate(-1px, -1px) scale(0.98);
      box-shadow: 1px 1px 0 0 var(--white-new);
      transition: all 80ms ease-out;
    }

    &:after {
      display: none !important;
    }
  `,

  bigButton: css`
    color: var(--white-new);
    background-color: transparent;
    border: 1px solid var(--white-new);
    border-radius: var(--border-radius);
    padding: 1.25rem 1.75rem;
    font-size: var(--fz-sm);
    font-family: var(--font-mono);
    line-height: 1;
    text-decoration: none;
    transition: all 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    overflow: hidden;
    will-change: transform, box-shadow;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
      transition: left 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    &:hover,
    &:focus-visible {
      outline: none;
      box-shadow: 3px 3px 0 0 var(--white-new);
      transform: translate(-3px, -3px) scale(1.02);

      &:before {
        left: 100%;
      }
    }

    &:active {
      transform: translate(-1px, -1px) scale(0.98);
      box-shadow: 1px 1px 0 0 var(--white-new);
      transition: all 100ms ease-out;
    }

    &:after {
      display: none !important;
    }
  `,

  boxShadow: css`
    box-shadow: 0 10px 30px -15px var(--navy-shadow);
    transition: all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform, box-shadow;

    &:hover,
    &:focus-visible {
      box-shadow: 0 20px 40px -15px var(--navy-shadow), 0 0 0 1px rgba(100, 255, 218, 0.1);
      transform: translateY(-2px) scale(1.01);
    }
  `,

  fancyList: css`
    padding: 0;
    margin: 0;
    list-style: none;
    font-size: var(--fz-lg);
    li {
      position: relative;
      padding-left: 30px;
      margin-bottom: 10px;
      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--white-new);
      }
    }
  `,

  resetList: css`
    list-style: none;
    padding: 0;
    margin: 0;
  `,
};

export default mixins;
